import { LocalFirestore } from "./LocalFirestore";
import { TournamentManager } from "./TournamentManager";
import { FirestoreTournamentRepository } from "./repositories/FirestoreTournamentRepository";
import { LocalTournamentRepository } from "./repositories/LocalTournamentRepository";
import { OfflineClassTournamentRepository } from "./repositories/OfflineClassTournamentRepository";
import { SwissPairingService, TournamentOrganizerPairingService, } from "./services/PairingService";
import { TournamentOrganizerStandingsCalculator } from "./services/StandingsCalculator";
import { TournamentGameService } from "./services/TournamentGameService";
import { TournamentPlayerService } from "./services/TournamentPlayerService";
import { TournamentRoundService } from "./services/TournamentRoundService";
import { TournamentScheduleService } from "./services/TournamentScheduleService";
export class TournamentManagerFactory {
    static create(repository, bbpPairingsExecutor) {
        // Initialize base services
        const gameService = new TournamentGameService(repository);
        const playerService = new TournamentPlayerService(repository);
        const standingsCalculator = new TournamentOrganizerStandingsCalculator();
        const scheduleService = new TournamentScheduleService(repository);
        // Initialize round service with its dependencies
        const roundService = new TournamentRoundService(repository, gameService, playerService, standingsCalculator);
        // Create pairing service factory function
        const createPairingService = (tournament) => {
            switch (tournament.type) {
                case "Swiss":
                    return new SwissPairingService(bbpPairingsExecutor);
                case "Robin":
                case "Elim":
                    return new TournamentOrganizerPairingService();
                default:
                    throw new Error(`Unsupported tournament type: ${tournament.type}`);
            }
        };
        // Create tournament manager with all services
        return new TournamentManager(repository, createPairingService, playerService, roundService, gameService, scheduleService);
    }
    static createForOnline(firestore, bbpPairingsExecutor, createMatchSQL) {
        // Initialize repository
        const repository = new FirestoreTournamentRepository(firestore, createMatchSQL);
        return this.create(repository, bbpPairingsExecutor);
    }
    static createForOfflineClass(firestore, bbpPairingsExecutor, serverTimestamp) {
        // Initialize repository
        const repository = new OfflineClassTournamentRepository(firestore, serverTimestamp);
        return this.create(repository, bbpPairingsExecutor);
    }
    static createForLocal(bbpPairingsExecutor, createMatchSQL) {
        // Mock implementation for local tournament
        const repository = new LocalTournamentRepository(LocalFirestore, createMatchSQL);
        return this.create(repository, bbpPairingsExecutor);
    }
    static createForTesting() {
        // Mock implementation for testing
        const mockCreateMatchSQL = async () => "mock-match-id";
        const repository = new LocalTournamentRepository(LocalFirestore, mockCreateMatchSQL);
        const mockBBPPairingsExecutor = async () => [];
        return this.create(repository, mockBBPPairingsExecutor);
    }
}
// Usage example:
/*
// initialize pairing api
// from frontend such as vue
import { firestore, functions } from "@/services/firebase";
import { TournamentManagerFactory } from "vue-chess-shared/tournament/TournamentManagerFactory";
const handleBBPPair = functions.httpsCallable("tournament-handleBBPPair");

// from backend such as firebase functions
import { getBBPPairs } from "./utils/bbp-utils.js";
const handleBBPPair = getBBPPairs;

// Then, for firestore tournament
const tournamentManager = TournamentManagerFactory.createForOnline(
  firestore,
  handleBBPPair
);

or

// for local tournament
const tournamentManager = TournamentManagerFactory.createForLocal(
  handleBBPPair
);

// Use the manager
await tournamentManager.startRound("tournamentId", 1);
*/
